import { useState } from 'react';
import type { Contract } from '@traceio-org/platform-contracts';
import type { ContractChange } from '@traceio-org/platform-client';
import { EmptyPageState, NavigationGlyph, PageSection } from '@traceio-org/ui';
import { formatMoney, microsToInput, parseMoneyToMicros } from '../../../billing/money';
import { AdminProblem } from '../../../components/AdminProblem';
import { LoadingBlock } from '../../../components/feedback';
import { RadioGroup, ReasonField, SelectField, TextField, reasonError } from '../../../components/fields';
import { DateText, ScrollTable, StatusBadge } from '../../../components/records';
import { WriteDialog } from '../../../components/WriteDialog';
import { useAdmin } from '../../../context';
import {
  BILLING_PROVIDER_LABELS,
  formatCount,
  formatDate,
  presentStatus,
  productName,
  versionLabel,
} from '../../../format';
import type { AdminRead } from '../../../useAdminRead';
import type { ContractList } from '@traceio-org/platform-client';
import { billingStopped, contractName, endOptions, periodLabel } from './shared';

const CAP_CHOICES = [
  { value: 'none', label: 'No spend cap', description: 'Usage is billed as it comes.' },
  {
    value: 'cap',
    label: 'A monthly spend cap',
    description: 'New work waits once the period reaches it; work already admitted finishes.',
  },
] as const;
type CapMode = (typeof CAP_CHOICES)[number]['value'];

/** The most carrier connections a contract may purchase (contractCreateSchema). */
const MAX_CONNECTIONS = 100_000;

function connectionsError(value: string): string | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return 'Enter a whole number of connections.';
  if (Number(trimmed) > MAX_CONNECTIONS) return `At most ${formatCount(MAX_CONNECTIONS)} connections.`;
  return null;
}

function capError(mode: CapMode, value: string): string | null {
  if (mode === 'none') return null;
  const micros = parseMoneyToMicros(value);
  if (micros === null) return 'Enter the cap as a number, for example 1500 or 1500.50.';
  if (micros <= 0) return 'The cap must be above zero.';
  return null;
}

/** Why a contract's billing stopped, for the warning above the table. */
function StoppedWarning({ contracts }: { contracts: readonly Contract[] }) {
  const stopped = contracts.filter(billingStopped);
  if (stopped.length === 0) return null;
  return (
    <div className="admin-billing-stopped" role="alert">
      <span className="admin-billing-stopped__icon" aria-hidden="true">
        <NavigationGlyph name="FileWarning" />
      </span>
      <div>
        <p>
          <strong>Billing stopped.</strong>{' '}
          {stopped.map((contract, index) => (
            <span key={contract.id}>
              {index > 0 ? ' ' : null}
              The {contractName(contract)} contract no longer collects at{' '}
              {BILLING_PROVIDER_LABELS[contract.provider?.name ?? ''] ?? contract.provider?.name} (status{' '}
              <code>{contract.provider?.status}</code>).
            </span>
          ))}
        </p>
        <p>
          No new work is admitted for it until collection is restored; usage of work already done is still
          recorded.
        </p>
      </div>
    </div>
  );
}

function ProviderCell({ contract }: { contract: Contract }) {
  if (!contract.provider) return <span className="admin-muted">Not linked</span>;
  const { name, status, customerRef, subscriptionRef } = contract.provider;
  const stopped = billingStopped(contract);
  return (
    <>
      {BILLING_PROVIDER_LABELS[name] ?? name}
      {status ? (
        <span className={stopped ? 'admin-cell-secondary admin-over-cap' : 'admin-cell-secondary'}>
          {stopped ? 'Collection stopped: ' : ''}
          {presentStatus(status).label}
        </span>
      ) : (
        <span className="admin-cell-secondary">No status yet</span>
      )}
      {customerRef || subscriptionRef ? (
        <span className="admin-cell-secondary">
          {customerRef ? <code>{customerRef}</code> : null}{' '}
          {subscriptionRef ? <code>{subscriptionRef}</code> : null}
        </span>
      ) : null}
    </>
  );
}

function ContractsTable({
  contracts,
  busy,
  canManage,
  onChange,
  onEndTrial,
}: {
  contracts: readonly Contract[];
  busy: boolean;
  canManage: boolean;
  onChange: (contract: Contract) => void;
  onEndTrial: (contract: Contract) => void;
}) {
  return (
    <ScrollTable caption="Contracts" busy={busy} className="admin-table admin-table--contracts">
      <thead>
        <tr>
          <th scope="col">Product</th>
          <th scope="col">Plan version</th>
          <th scope="col">Status</th>
          <th scope="col">Starts</th>
          <th scope="col">Ends</th>
          <th scope="col">Trial</th>
          <th scope="col">Current period</th>
          <th scope="col" className="admin-num">
            Connections
          </th>
          <th scope="col">Spend cap</th>
          <th scope="col">Provider</th>
          {canManage ? <th scope="col">Actions</th> : null}
        </tr>
      </thead>
      <tbody>
        {contracts.map((contract) => (
          <tr key={contract.id} data-billing-stopped={billingStopped(contract) || undefined}>
            <th scope="row">
              {productName(contract.productKey)}
              <span className="admin-cell-secondary">
                <code className="admin-id">{contract.id}</code>
              </span>
            </th>
            <td className="admin-cell-wrap">
              {contract.planVersion.planName} {versionLabel(contract.planVersion.version)}
              <span className="admin-cell-secondary">
                <code>{contract.planVersion.planKey}</code>
              </span>
            </td>
            <td>
              <StatusBadge status={contract.status} />
            </td>
            <td>
              <DateText iso={contract.startsAt} />
            </td>
            <td>
              {contract.endsAt ? (
                <DateText iso={contract.endsAt} />
              ) : (
                <span className="admin-muted">No end date</span>
              )}
            </td>
            <td>
              {contract.trialEndsAt ? (
                <>
                  {contract.trialEndedEarly ? 'Ended early ' : 'Until '}
                  <DateText iso={contract.trialEndsAt} />
                  <span className="admin-cell-secondary">
                    {contract.trialCardRequired ? 'Card required' : 'No card required'}
                  </span>
                </>
              ) : (
                <span className="admin-muted">None</span>
              )}
            </td>
            <td>
              {contract.currentPeriod ? (
                periodLabel(contract.currentPeriod)
              ) : (
                <span className="admin-muted">
                  {contract.status === 'scheduled' ? 'Not started' : 'Ended'}
                </span>
              )}
            </td>
            <td className="admin-num">{formatCount(contract.carrierConnections)}</td>
            <td>
              {contract.spendCap ? (
                `${formatMoney(contract.spendCap.amountMicros, contract.spendCap.currency)} a month`
              ) : (
                <span className="admin-muted">No cap</span>
              )}
            </td>
            <td>
              <ProviderCell contract={contract} />
            </td>
            {canManage ? (
              <td>
                {contract.status === 'ended' ? (
                  <span className="admin-muted">None</span>
                ) : (
                  <div className="admin-row-actions">
                    <button
                      type="button"
                      className="admin-action"
                      aria-label={`Change the ${contractName(contract)} contract`}
                      onClick={() => onChange(contract)}
                    >
                      Change
                    </button>
                    {contract.status === 'trialing' ? (
                      <button
                        type="button"
                        className="admin-action"
                        aria-label={`End the ${contractName(contract)} trial now`}
                        onClick={() => onEndTrial(contract)}
                      >
                        End the trial now
                      </button>
                    ) : null}
                  </div>
                )}
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

interface ChangeForm {
  capMode: CapMode;
  cap: string;
  connections: string;
  endsAt: string;
  reason: string;
}
type ChangeErrors = Partial<Record<keyof ChangeForm, string>>;

function changeFormOf(contract: Contract): ChangeForm {
  return {
    capMode: contract.spendCap ? 'cap' : 'none',
    cap: contract.spendCap ? microsToInput(contract.spendCap.amountMicros) : '',
    connections: String(contract.carrierConnections),
    endsAt: contract.endsAt ?? '',
    reason: '',
  };
}

/**
 * Changing a contract: the spend cap, the carrier connections or the end (a period boundary after now). Only what
 * changed is sent, with the version read, so two staff members never overwrite each other unseen. A lowered cap
 * blocks new admissions only; the dialog says so. On a conflict the page reads the contract again; when it arrives
 * with another version, the form starts over from it (keeping the reason), so a retry never sends values typed
 * against the old copy, which would undo the other change unseen.
 */
export function ChangeContractDialog({
  organizationId,
  contract: latest,
  open,
  onClose,
  onSaved,
  onConflict,
}: {
  organizationId: string;
  /** The contract as the page last read it. */
  contract: Contract;
  open: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
  onConflict: () => void;
}) {
  const { api } = useAdmin();
  const [contract, setContract] = useState(latest);
  const [form, setForm] = useState<ChangeForm>(() => changeFormOf(latest));
  const [errors, setErrors] = useState<ChangeErrors>({});
  const [restarted, setRestarted] = useState(false);
  if (latest.version !== contract.version) {
    // Adjusting state while rendering (React's documented pattern for state derived from a changed prop).
    setContract(latest);
    setForm((current) => ({ ...changeFormOf(latest), reason: current.reason }));
    setErrors({});
    setRestarted(true);
  }
  const set = <K extends keyof ChangeForm>(field: K, value: ChangeForm[K]) =>
    setForm((current) => ({ ...current, [field]: value }));
  const ends = [
    { value: '', label: 'No end date' },
    ...endOptions(contract, Date.now()),
    // The end the contract already has stays choosable even when it is not among the next boundaries listed.
    ...(contract.endsAt && !endOptions(contract, Date.now()).some((o) => o.value === contract.endsAt)
      ? [{ value: contract.endsAt, label: `${formatDate(contract.endsAt)} (the current end)` }]
      : []),
  ];

  const build = (): ContractChange | null => {
    const found: ChangeErrors = {};
    const cap = capError(form.capMode, form.cap);
    if (cap) found.cap = cap;
    const connections = connectionsError(form.connections);
    if (connections) found.connections = connections;
    const reason = reasonError(form.reason);
    if (reason) found.reason = reason;
    const nextCap = form.capMode === 'none' ? null : parseMoneyToMicros(form.cap);
    const currentCap = contract.spendCap?.amountMicros ?? null;
    const nextConnections = Number(form.connections.trim());
    const nextEnd = form.endsAt || null;
    const change: ContractChange = { expectedVersion: contract.version, reason: form.reason };
    if (!cap && nextCap !== currentCap) change.spendCapMicros = nextCap;
    if (!connections && nextConnections !== contract.carrierConnections)
      change.carrierConnections = nextConnections;
    if (nextEnd !== contract.endsAt) change.endsAt = nextEnd;
    if (
      !cap &&
      !connections &&
      change.spendCapMicros === undefined &&
      change.carrierConnections === undefined &&
      change.endsAt === undefined
    ) {
      found.cap = 'Change the spend cap, the carrier connections or the end.';
    }
    setErrors(found);
    return Object.keys(found).length > 0 ? null : change;
  };

  return (
    <WriteDialog
      open={open}
      onClose={onClose}
      title={`Change the ${contractName(contract)} contract`}
      description="Only what you change is sent. A lowered cap blocks new admissions only; work already admitted finishes."
      submitLabel="Save the contract"
      onSubmit={async () => {
        const change = build();
        if (!change) return 'invalid';
        await api.updateContract(organizationId, contract.id, change);
        return undefined;
      }}
      onSaved={() => onSaved(`The ${contractName(contract)} contract is saved.`)}
      onFailed={(failure) => {
        if (failure.kind === 'conflict') onConflict();
      }}
    >
      {restarted ? (
        <p className="admin-dialog-warning" role="status">
          The contract changed meanwhile. The form now shows it as it is: make your change again.
        </p>
      ) : null}
      <RadioGroup
        legend="Spend cap"
        choices={CAP_CHOICES}
        value={form.capMode}
        onChange={(value) => set('capMode', value as CapMode)}
        error={form.capMode === 'none' ? (errors.cap ?? null) : null}
      />
      {form.capMode === 'cap' ? (
        <TextField
          label="Monthly spend cap"
          value={form.cap}
          onChange={(value) => set('cap', value)}
          hint="In the contract's currency, usage only; the base and the connections are billed regardless. It must leave room for the token price's minimum charge."
          error={errors.cap ?? null}
          inputMode="decimal"
          required
        />
      ) : null}
      <TextField
        label="Carrier connections"
        value={form.connections}
        onChange={(value) => set('connections', value)}
        type="number"
        min={0}
        max={MAX_CONNECTIONS}
        hint="The purchased number; a period bills the most purchased while it ran."
        error={errors.connections ?? null}
        inputMode="numeric"
        required
      />
      <SelectField
        label="Ends"
        value={form.endsAt}
        options={ends}
        onChange={(value) => set('endsAt', value)}
        hint="A contract ends at the end of one of its periods, after now."
      />
      <ReasonField
        value={form.reason}
        onChange={(value) => set('reason', value)}
        error={errors.reason ?? null}
      />
    </WriteDialog>
  );
}

/**
 * Ending a running trial now, so billing starts now (spec section 2.5). The customer must have a card on file at the
 * billing provider; platform-api refuses otherwise and the dialog shows why. Sent with the version read: on a
 * conflict the page reads the contract again.
 */
export function EndTrialDialog({
  organizationId,
  contract,
  open,
  onClose,
  onSaved,
  onConflict,
}: {
  organizationId: string;
  contract: Contract;
  open: boolean;
  onClose: () => void;
  onSaved: (message: string) => void;
  onConflict: () => void;
}) {
  const { api } = useAdmin();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <WriteDialog
      open={open}
      onClose={onClose}
      title={`End the ${contractName(contract)} trial now`}
      description={`The trial${contract.trialEndsAt ? `, planned until ${formatDate(contract.trialEndsAt)},` : ''} ends now and billing starts now: the billing provider charges the first paid period to the customer's card at once, and later periods renew on today's date each month. The customer must have a card on file; without one, ask an organization administrator to add one through Manage payment on their billing page first. This cannot be undone.`}
      submitLabel="End the trial now"
      onSubmit={async () => {
        const found = reasonError(reason);
        setError(found);
        if (found) return 'invalid';
        await api.endContractTrial(organizationId, contract.id, contract.version, reason);
        return undefined;
      }}
      onSaved={() => onSaved(`The ${contractName(contract)} trial ended; billing started now.`)}
      onFailed={(failure) => {
        if (failure.kind === 'conflict') onConflict();
      }}
    >
      <ReasonField value={reason} onChange={setReason} error={error} />
    </WriteDialog>
  );
}

export interface ContractsSectionProps {
  read: AdminRead<ContractList>;
  contracts: readonly Contract[] | null;
  canManage: boolean;
  onSign: () => void;
  onChange: (contract: Contract) => void;
  onEndTrial: (contract: Contract) => void;
}

/** The contracts of the organization, with a warning for any whose billing stopped; sign and change for staff.billing.manage. */
export function ContractsSection({
  read,
  contracts,
  canManage,
  onSign,
  onChange,
  onEndTrial,
}: ContractsSectionProps) {
  return (
    <PageSection
      title="Contracts"
      description="The organization's agreements per product: the plan version, the trial, the spend cap and the provider's view of them."
      action={
        canManage ? (
          <button type="button" className="admin-action admin-action--primary" onClick={onSign}>
            Sign a contract
          </button>
        ) : undefined
      }
    >
      {read.failure ? (
        <AdminProblem failure={read.failure} subject="the contracts" onRetry={read.retry} />
      ) : !contracts ? (
        <LoadingBlock label="Loading the contracts" />
      ) : contracts.length === 0 ? (
        <EmptyPageState
          icon={<NavigationGlyph name="WalletCards" />}
          title="No contract yet"
          description="Nothing is billed until a contract is signed on a published plan version."
        />
      ) : (
        <>
          <StoppedWarning contracts={contracts} />
          <ContractsTable
            contracts={contracts}
            busy={read.loading}
            canManage={canManage}
            onChange={onChange}
            onEndTrial={onEndTrial}
          />
        </>
      )}
    </PageSection>
  );
}
